import { Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, first } from 'rxjs';
import { Navbar } from './shared/components/navbar/navbar.component';
import {
  ADMIN_NAV_ITEMS,
  TENANT_NAV_ITEMS,
} from './shared/components/navbar/navbar.items';
import { Topbar } from './shared/components/topbar/topbar';
import { Toast } from './shared/components/toast/toast.component';
import { Breadcrumbs } from './shared/components/breadcrumbs/breadcrumbs.component';
import { lockBodyScroll, unlockBodyScroll } from './shared/utils/body-scroll-lock';
import { ThemeService } from './core/theme/theme.service';
import { PortalService } from './core/portal.service';
import { ViewportService } from './core/services/viewport.service';
import { dismissSplash, startSplashWatchdog } from './core/splash/splash';
import { ErrorService } from './core/errors/error.service';
import { ErrorCode } from './core/errors/error.model';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Navbar, Topbar, Toast, Breadcrumbs],
  templateUrl: './app.html',
  styleUrl: './app.css',
  host: {
    '(document:keydown.escape)': 'closeNavDrawer()',
  },
})
export class AppComponent {
  private readonly portalService = inject(PortalService);
  private readonly viewport = inject(ViewportService);

  // The admin back-office and tenant app render different nav item sets.
  protected readonly navbarItems = computed(() =>
    this.portalService.inAdminPortal() ? ADMIN_NAV_ITEMS : TENANT_NAV_ITEMS,
  );

  /** Phone-shell nav drawer (the static sidebar is hidden below md). */
  protected readonly navDrawerOpen = signal(false);
  private drawerLocked = false;

  /** True on routes flagged `data: { bareLayout: true }` (the error pages):
   *  the shell chrome is hidden because their context is broken or absent. */
  protected readonly bareLayout = signal(false);

  // Eager injection applies the persisted theme to <html> on app load, before
  // any component that offers the toggle is rendered. Injected in the
  // constructor (not a field) so it isn't tree-shaken as an unused member.
  constructor() {
    inject(ThemeService);

    const router = inject(Router);

    // Nav-link taps navigate; close the drawer without touching the navbar.
    // The same completed navigation resolves the bare-layout flag from the
    // deepest activated route's data.
    router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.navDrawerOpen.set(false);
        let route = router.routerState.snapshot.root;
        while (route.firstChild) route = route.firstChild;
        this.bareLayout.set(route.data['bareLayout'] === true);
      });

    // The boot splash (index.html) stays up until the first navigation
    // completes. Every non-error route is gated on ContextService (root
    // redirect guard, OrganizationGuard, hasRoleGuard), so this fires only
    // once auth + /me context are resolved — or when an unguarded error page
    // (/not-found, /no-organization, /load-failed) resolves, which must also
    // be revealed. On the unauthenticated path no navigation ever completes;
    // the splash stays up until authorize() redirects to the IdP. If nothing
    // completes AND no redirect happens (context dead-end), the watchdog
    // navigates to the load-failed page, whose own NavigationEnd then lands
    // here and dismisses the splash.
    const errorService = inject(ErrorService);
    const cancelWatchdog = startSplashWatchdog(() =>
      errorService.navigateToError(ErrorCode.LOAD_FAILED),
    );
    router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        first(),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        cancelWatchdog();
        dismissSplash();
      });
    inject(DestroyRef).onDestroy(cancelWatchdog);

    // Rotating a tablet past md while the drawer is open: the static sidebar
    // takes over, the drawer must not linger beneath it.
    effect(() => {
      if (!this.viewport.isMobile()) this.navDrawerOpen.set(false);
    });

    // Ref-counted with modals, so a dialog opened from the drawer stays locked.
    effect(() => {
      const open = this.navDrawerOpen();
      if (open && !this.drawerLocked) {
        this.drawerLocked = true;
        lockBodyScroll();
      } else if (!open && this.drawerLocked) {
        this.drawerLocked = false;
        unlockBodyScroll();
      }
    });
    inject(DestroyRef).onDestroy(() => {
      if (this.drawerLocked) unlockBodyScroll();
    });
  }

  protected closeNavDrawer(): void {
    this.navDrawerOpen.set(false);
  }

}
